import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardPageFormToggleSection,
} from '@shigo-ui/dashboard-ui';
import { Input } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { PublisherFormValue } from '../../publisherFormSchema';

export function PublisherCompanyFields({ campaignFeatureAllowed }: PublisherCompanyFieldsProps) {
  'use no memo';
  const { control } = useFormContext<PublisherFormValue>();
  const gamIdEnabled = useWatch({ control, name: 'gamIdEnabled' });
  return (
    <DashboardPageFormBody width="lg">
      <Controller
        control={control}
        name="companyName"
        render={({ field, fieldState }) => (
          <DashboardPageFormField
            label={dashboardT('publishers.companyName')}
            htmlFor="publisher-company-name"
            required
            error={fieldState.error?.message}
          >
            <Input
              {...field}
              id="publisher-company-name"
              required
              aria-invalid={Boolean(fieldState.error)}
            />
          </DashboardPageFormField>
        )}
      />
      {campaignFeatureAllowed ? (
        <div className="grid gap-5 md:grid-cols-2">
          <Controller
            control={control}
            name="gamIdEnabled"
            render={({ field }) => (
              <DashboardPageFormToggleSection
                label={dashboardT('publishers.gamEnabled')}
                checked={field.value}
                onCheckedChange={field.onChange}
              >
                <Controller
                  control={control}
                  name="gamAdUnitId"
                  render={({ field: inputField, fieldState }) => (
                    <DashboardPageFormField
                      label={dashboardT('publishers.gamId')}
                      htmlFor="publisher-gam-id"
                      required={gamIdEnabled}
                      error={fieldState.error?.message}
                    >
                      <Input
                        {...inputField}
                        id="publisher-gam-id"
                        required={gamIdEnabled}
                        aria-invalid={Boolean(fieldState.error)}
                      />
                    </DashboardPageFormField>
                  )}
                />
              </DashboardPageFormToggleSection>
            )}
          />
          <Controller
            control={control}
            name="mcmActive"
            render={({ field }) => (
              <DashboardPageFormToggleSection
                label={dashboardT('publishers.mcm')}
                checked={field.value}
                onCheckedChange={field.onChange}
              >
                <p className="text-muted-foreground text-sm">
                  {dashboardT('publishers.mcmHelper')}
                </p>
              </DashboardPageFormToggleSection>
            )}
          />
        </div>
      ) : null}
      <div className="grid gap-5 md:grid-cols-2">
        <Controller
          control={control}
          name="contactName"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('publishers.contactName')}
              htmlFor="publisher-contact-name"
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                id="publisher-contact-name"
                required
                aria-invalid={Boolean(fieldState.error)}
              />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('publishers.phone')}
              htmlFor="publisher-phone"
              error={fieldState.error?.message}
            >
              <Input {...field} id="publisher-phone" aria-invalid={Boolean(fieldState.error)} />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="websiteUrl"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('publishers.website')}
              htmlFor="publisher-website"
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                id="publisher-website"
                required
                aria-invalid={Boolean(fieldState.error)}
              />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('publishers.email')}
              htmlFor="publisher-email"
              required
              error={fieldState.error?.message}
            >
              <Input
                {...field}
                id="publisher-email"
                type="email"
                required
                aria-invalid={Boolean(fieldState.error)}
              />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="address"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('publishers.address')}
              htmlFor="publisher-address"
              error={fieldState.error?.message}
            >
              <Input {...field} id="publisher-address" aria-invalid={Boolean(fieldState.error)} />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="city"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('publishers.city')}
              htmlFor="publisher-city"
              error={fieldState.error?.message}
            >
              <Input {...field} id="publisher-city" aria-invalid={Boolean(fieldState.error)} />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="state"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('publishers.state')}
              htmlFor="publisher-state"
              error={fieldState.error?.message}
            >
              <Input {...field} id="publisher-state" aria-invalid={Boolean(fieldState.error)} />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="zipCode"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('publishers.zipCode')}
              htmlFor="publisher-zip-code"
              error={fieldState.error?.message}
            >
              <Input {...field} id="publisher-zip-code" aria-invalid={Boolean(fieldState.error)} />
            </DashboardPageFormField>
          )}
        />
        <Controller
          control={control}
          name="country"
          render={({ field, fieldState }) => (
            <DashboardPageFormField
              label={dashboardT('publishers.country')}
              htmlFor="publisher-country"
              error={fieldState.error?.message}
            >
              <Input {...field} id="publisher-country" aria-invalid={Boolean(fieldState.error)} />
            </DashboardPageFormField>
          )}
        />
      </div>
    </DashboardPageFormBody>
  );
}

interface PublisherCompanyFieldsProps {
  campaignFeatureAllowed: boolean;
}
